<template>
  <div class="technical-indicators">
    <div class="indicators-header">
      <h4>{{ panelCopy.title }}</h4>
      <div class="indicator-controls">
        <button 
          @click="toggleIndicatorSelector" 
          class="btn btn-sm indicator-selector-btn"
        >
          {{ format(panelCopy.selectButton, { count: selectedIndicators.length }) }}
        </button>
        <button 
          @click="refreshIndicators" 
          :disabled="isLoading" 
          class="btn btn-sm"
        >
          {{ isLoading ? panelCopy.refreshing : panelCopy.refreshButton }}
        </button>
      </div>
    </div>

    <!-- 指标配置器 -->
    <div v-if="showIndicatorSelector" class="indicator-selector-overlay">
      <IndicatorConfig
        v-model="selectedIndicators"
        :indicatorCategories="indicatorCategories"
        @selection-change="onIndicatorSelectionChange"
        @settings-change="onSettingsChange"
        @close="toggleIndicatorSelector"
      />
    </div>

    <!-- 指标图表区域 -->
    <div v-if="selectedIndicators.length > 0" class="indicators-charts">
      <div 
        v-for="indicator in activeIndicators" 
        :key="indicator.key"
        class="indicator-chart-container"
      >
        <div class="chart-header">
          <h5>{{ indicator.name }}</h5>
          <button 
            @click="removeIndicator(indicator.key)"
            class="remove-indicator-btn"
            :title="format(panelCopy.removeTooltip, { name: indicator.name })"
          >
            ×
          </button>
        </div>
        <div class="chart-wrapper" :style="{ height: displaySettings.chartHeight + 'px' }">
          <DataChart 
            v-if="indicator.chartOptions" 
            :key="`${indicator.key}-${chartUpdateKey}`"
            :options="indicator.chartOptions" 
          />
          <div v-else-if="isLoading" class="loading-message">
            {{ format(panelCopy.loadingIndicator, { name: indicator.name }) }}
          </div>
          <div v-else class="no-data-message">
            {{ format(panelCopy.noIndicator, { name: indicator.name }) }}
          </div>
        </div>
      </div>
    </div>

    <div v-else-if="!isLoading" class="no-indicators-message">
      <p>{{ panelCopy.noSelectionPrompt }}</p>
      <button @click="toggleIndicatorSelector" class="btn">{{ panelCopy.chooseButton }}</button>
    </div>

    <div v-if="isLoading && selectedIndicators.length === 0" class="loading-message">
      {{ panelCopy.initialLoading }}
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import DataChart from '@/components/DataChart.vue';
import IndicatorConfig from './IndicatorConfig.vue';
import { simulationIndicatorsContent } from '@/content/simulationIndicators';
import { formatMessage } from '@/utils/formatMessage';

const props = defineProps({
  kLineData: {
    type: Array,
    default: () => []
  },
  assetCode: {
    type: String,
    default: ''
  }
});

const emit = defineEmits(['loading-change']);

const { locale } = useI18n();

const localeCopy = computed(
  () => simulationIndicatorsContent[locale.value] ?? simulationIndicatorsContent['en-US']
);
const panelCopy = computed(() => localeCopy.value.panel);
const indicatorDefinitions = computed(() => localeCopy.value.indicators.definitions);
const indicatorCategories = computed(() =>
  localeCopy.value.indicators.categories.map((category) => ({
    key: category.key,
    name: category.title,
    indicators: category.indicators.map((key) => {
      const definition = indicatorDefinitions.value[key] || {};
      return {
        key,
        name: definition.name ?? key,
        description: definition.description ?? '',
        params: definition.params ?? []
      };
    })
  }))
);

const format = (template, params) => formatMessage(template, params);

// 响应式数据
const showIndicatorSelector = ref(false);
const selectedIndicators = ref(['macd', 'rsi']); // 默认选择MACD和RSI
const isLoading = ref(false);
const chartUpdateKey = ref(0);
const displaySettings = ref({
  syncTimeAxis: true,
  showDataLabels: false,
  chartHeight: 300
});

// 计算属性：当前激活的指标
const activeIndicators = computed(() =>
  selectedIndicators.value
    .map((key) => {
      const definition = indicatorDefinitions.value[key];
      if (!definition) return null;
      return {
        key,
        name: definition.name ?? key,
        description: definition.description ?? '',
        params: definition.params ?? [],
        chartOptions: generateChartOptions(key)
      };
    })
    .filter(Boolean)
);

// 生成图表配置
const generateChartOptions = (indicatorKey) => {
  if (!props.kLineData?.length) return null;

  const dates = props.kLineData.map(item => item.date);
  
  switch (indicatorKey) {
    case 'macd':
      return generateMACDChart(dates);
    case 'rsi':
      return generateRSIChart(dates);
    case 'bollinger':
      return generateBollingerChart(dates);
    case 'adx':
      return generateADXChart(dates);
    case 'aroon':
      return generateAroonChart(dates);
    case 'obv':
      return generateOBVChart(dates);
    case 'cmf':
      return generateCMFChart(dates);
    case 'volume':
      return generateVolumeChart(dates);
    default:
      return null;
  }
};

// MACD图表配置
const generateMACDChart = (dates) => {
  const definition = indicatorDefinitions.value.macd || {};
  const legend = definition.legend || { macd: 'MACD', signal: 'Signal', histogram: 'Histogram' };

  const macdData = props.kLineData.map((item) => item.macd || 0);
  const signalData = props.kLineData.map((item) => item.signal_line || 0);
  const histogramData = props.kLineData.map(
    (item) => (item.macd || 0) - (item.signal_line || 0)
  );

  return {
    title: {
      text: definition.name ?? 'MACD',
      left: 'center',
      textStyle: { fontSize: 14 }
    },
    tooltip: {
      trigger: 'axis',
      formatter(params) {
        let result = `${params[0].name}<br/>`;
        params.forEach((param) => {
          result += `${param.seriesName}: ${param.value.toFixed(4)}<br/>`;
        });
        return result;
      }
    },
    legend: {
      data: [legend.macd, legend.signal, legend.histogram],
      top: 25
    },
    xAxis: {
      type: 'category',
      data: dates,
      axisLabel: { fontSize: 10 }
    },
    yAxis: {
      type: 'value',
      axisLabel: { fontSize: 10 }
    },
    series: [
      {
        name: legend.macd,
        type: 'line',
        data: macdData,
        lineStyle: { color: '#1890ff', width: 2 },
        symbol: 'none'
      },
      {
        name: legend.signal,
        type: 'line',
        data: signalData,
        lineStyle: { color: '#ff4d4f', width: 2 },
        symbol: 'none'
      },
      {
        name: legend.histogram,
        type: 'bar',
        data: histogramData,
        itemStyle: {
          color(params) {
            return params.value >= 0 ? '#52c41a' : '#ff4d4f';
          }
        }
      }
    ]
  };
};

// RSI图表配置
const generateRSIChart = (dates) => {
  const definition = indicatorDefinitions.value.rsi || {};
  const legend = definition.legend || { rsi: 'RSI' };
  const rsiData = props.kLineData.map((item) => item.rsi || 0);

  return {
    title: {
      text: definition.name ?? 'RSI',
      left: 'center',
      textStyle: { fontSize: 14 }
    },
    tooltip: {
      trigger: 'axis',
      formatter(params) {
        return `${params[0].name}<br/>${legend.rsi}: ${params[0].value.toFixed(2)}`;
      }
    },
    xAxis: {
      type: 'category',
      data: dates,
      axisLabel: { fontSize: 10 }
    },
    yAxis: {
      type: 'value',
      min: 0,
      max: 100,
      axisLabel: { fontSize: 10 }
    },
    series: [
      {
        name: legend.rsi,
        type: 'line',
        data: rsiData,
        lineStyle: { color: '#722ed1', width: 2 },
        symbol: 'none',
        markLine: {
          data: [
            { yAxis: 70, lineStyle: { color: '#ff4d4f', type: 'dashed' } },
            { yAxis: 30, lineStyle: { color: '#52c41a', type: 'dashed' } }
          ]
        }
      }
    ]
  };
};

// 布林带图表配置
const generateBollingerChart = (dates) => {
  const definition = indicatorDefinitions.value.bollinger || {};
  const legend = definition.legend || {
    close: 'Close price',
    upper: 'Upper band',
    lower: 'Lower band'
  };

  const upperBand = props.kLineData.map((item) => item.upper_band || 0);
  const lowerBand = props.kLineData.map((item) => item.lower_band || 0);
  const closePrice = props.kLineData.map((item) => item.close_price || 0);

  return {
    title: {
      text: definition.name ?? 'Bollinger Bands',
      left: 'center',
      textStyle: { fontSize: 14 }
    },
    tooltip: {
      trigger: 'axis',
      formatter(params) {
        let result = `${params[0].name}<br/>`;
        params.forEach((param) => {
          result += `${param.seriesName}: ¥${param.value.toFixed(2)}<br/>`;
        });
        return result;
      }
    },
    legend: {
      data: [legend.close, legend.upper, legend.lower],
      top: 25
    },
    xAxis: {
      type: 'category',
      data: dates,
      axisLabel: { fontSize: 10 }
    },
    yAxis: {
      type: 'value',
      axisLabel: { fontSize: 10 }
    },
    series: [
      {
        name: legend.close,
        type: 'line',
        data: closePrice,
        lineStyle: { color: '#1890ff', width: 2 },
        symbol: 'none'
      },
      {
        name: legend.upper,
        type: 'line',
        data: upperBand,
        lineStyle: { color: '#ff4d4f', width: 1 },
        symbol: 'none'
      },
      {
        name: legend.lower,
        type: 'line',
        data: lowerBand,
        lineStyle: { color: '#52c41a', width: 1 },
        symbol: 'none'
      }
    ]
  };
};

// ADX图表配置
const generateADXChart = (dates) => {
  const definition = indicatorDefinitions.value.adx || {};
  const legend = definition.legend || { adx: 'ADX' };
  const adxData = props.kLineData.map((item) => item.adx || 0);

  return {
    title: {
      text: definition.name ?? 'ADX',
      left: 'center',
      textStyle: { fontSize: 14 }
    },
    tooltip: {
      trigger: 'axis',
      formatter(params) {
        return `${params[0].name}<br/>${legend.adx}: ${params[0].value.toFixed(2)}`;
      }
    },
    xAxis: {
      type: 'category',
      data: dates,
      axisLabel: { fontSize: 10 }
    },
    yAxis: {
      type: 'value',
      min: 0,
      max: 100,
      axisLabel: { fontSize: 10 }
    },
    series: [
      {
        name: legend.adx,
        type: 'line',
        data: adxData,
        lineStyle: { color: '#fa8c16', width: 2 },
        symbol: 'none',
        markLine: {
          data: [
            { yAxis: 25, lineStyle: { color: '#ff4d4f', type: 'dashed' } }
          ]
        }
      }
    ]
  };
};

// Aroon图表配置
const generateAroonChart = (dates) => {
  const definition = indicatorDefinitions.value.aroon || {};
  const legend = definition.legend || { up: 'Aroon Up', down: 'Aroon Down' };
  const aroonUp = props.kLineData.map((item) => item.aroon_up || 0);
  const aroonDown = props.kLineData.map((item) => item.aroon_down || 0);

  return {
    title: {
      text: definition.name ?? 'Aroon',
      left: 'center',
      textStyle: { fontSize: 14 }
    },
    tooltip: {
      trigger: 'axis',
      formatter(params) {
        let result = `${params[0].name}<br/>`;
        params.forEach((param) => {
          result += `${param.seriesName}: ${param.value.toFixed(2)}<br/>`;
        });
        return result;
      }
    },
    legend: {
      data: [legend.up, legend.down],
      top: 25
    },
    xAxis: {
      type: 'category',
      data: dates,
      axisLabel: { fontSize: 10 }
    },
    yAxis: {
      type: 'value',
      min: 0,
      max: 100,
      axisLabel: { fontSize: 10 }
    },
    series: [
      {
        name: legend.up,
        type: 'line',
        data: aroonUp,
        lineStyle: { color: '#52c41a', width: 2 },
        symbol: 'none'
      },
      {
        name: legend.down,
        type: 'line',
        data: aroonDown,
        lineStyle: { color: '#ff4d4f', width: 2 },
        symbol: 'none'
      }
    ]
  };
};

// OBV图表配置
const generateOBVChart = (dates) => {
  const definition = indicatorDefinitions.value.obv || {};
  const legend = definition.legend || { obv: 'OBV' };
  const obvData = props.kLineData.map((item) => item.obv_ln || 0);

  return {
    title: {
      text: definition.name ?? 'OBV',
      left: 'center',
      textStyle: { fontSize: 14 }
    },
    tooltip: {
      trigger: 'axis',
      formatter(params) {
        return `${params[0].name}<br/>${legend.obv}: ${params[0].value.toFixed(4)}`;
      }
    },
    xAxis: {
      type: 'category',
      data: dates,
      axisLabel: { fontSize: 10 }
    },
    yAxis: {
      type: 'value',
      axisLabel: { fontSize: 10 }
    },
    series: [
      {
        name: legend.obv,
        type: 'line',
        data: obvData,
        lineStyle: { color: '#13c2c2', width: 2 },
        symbol: 'none'
      }
    ]
  };
};

// CMF图表配置
const generateCMFChart = (dates) => {
  const definition = indicatorDefinitions.value.cmf || {};
  const legend = definition.legend || { cmf: 'CMF' };
  const cmfData = props.kLineData.map((item) => item.cmf || 0);

  return {
    title: {
      text: definition.name ?? 'CMF',
      left: 'center',
      textStyle: { fontSize: 14 }
    },
    tooltip: {
      trigger: 'axis',
      formatter(params) {
        return `${params[0].name}<br/>${legend.cmf}: ${params[0].value.toFixed(4)}`;
      }
    },
    xAxis: {
      type: 'category',
      data: dates,
      axisLabel: { fontSize: 10 }
    },
    yAxis: {
      type: 'value',
      axisLabel: { fontSize: 10 }
    },
    series: [
      {
        name: legend.cmf,
        type: 'bar',
        data: cmfData,
        itemStyle: {
          color(params) {
            return params.value >= 0 ? '#52c41a' : '#ff4d4f';
          }
        }
      }
    ]
  };
};

// 成交量图表配置
const generateVolumeChart = (dates) => {
  const definition = indicatorDefinitions.value.volume || {};
  const legend = definition.legend || { volume: 'Volume' };
  const volumeData = props.kLineData.map((item) => item.volume || 0);

  return {
    title: {
      text: definition.name ?? 'Volume',
      left: 'center',
      textStyle: { fontSize: 14 }
    },
    tooltip: {
      trigger: 'axis',
      formatter(params) {
        return `${params[0].name}<br/>${legend.volume}: ${params[0].value.toLocaleString()}`;
      }
    },
    xAxis: {
      type: 'category',
      data: dates,
      axisLabel: { fontSize: 10 }
    },
    yAxis: {
      type: 'value',
      axisLabel: { fontSize: 10 }
    },
    series: [
      {
        name: legend.volume,
        type: 'bar',
        data: volumeData,
        itemStyle: { color: '#1890ff' }
      }
    ]
  };
};

// 方法
const toggleIndicatorSelector = () => {
  showIndicatorSelector.value = !showIndicatorSelector.value;
};

const onIndicatorSelectionChange = (newSelection) => {
  if (newSelection) {
    selectedIndicators.value = newSelection;
  }
  nextTick(() => {
    chartUpdateKey.value++;
  });
};

const onSettingsChange = (newSettings) => {
  displaySettings.value = { ...displaySettings.value, ...newSettings };
  nextTick(() => {
    chartUpdateKey.value++;
  });
};

const removeIndicator = (indicatorKey) => {
  const index = selectedIndicators.value.indexOf(indicatorKey);
  if (index > -1) {
    selectedIndicators.value.splice(index, 1);
    nextTick(() => {
      chartUpdateKey.value++;
    });
  }
};

const refreshIndicators = () => {
  isLoading.value = true;
  emit('loading-change', true);
  
  // 模拟刷新延迟
  setTimeout(() => {
    chartUpdateKey.value++;
    isLoading.value = false;
    emit('loading-change', false);
  }, 500);
};

// 监听数据变化
watch(() => props.kLineData, () => {
  nextTick(() => {
    chartUpdateKey.value++;
  });
}, { deep: true });
</script>

<style scoped>
.technical-indicators {
  margin-top: 24px;
  border-top: 1px solid #e8e8e8;
  padding-top: 20px;
}

.indicators-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.indicators-header h4 {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #262626;
}

.indicator-controls {
  display: flex;
  gap: 8px;
}

.btn {
  padding: 6px 12px;
  border-radius: 6px;
  border: 1px solid #d9d9d9;
  background: white;
  cursor: pointer;
  font-size: 12px;
  transition: all 0.2s;
}

.btn:hover:not(:disabled) {
  border-color: #1890ff;
  color: #1890ff;
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.indicator-selector-btn {
  background: #f0f8ff;
  border-color: #1890ff;
  color: #1890ff;
}

.indicator-selector-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0,0,0,0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
}



.indicators-charts {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.indicator-chart-container {
  background: white;
  border: 1px solid #e8e8e8;
  border-radius: 8px;
  overflow: hidden;
}

.chart-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  background: #fafafa;
  border-bottom: 1px solid #e8e8e8;
}

.chart-header h5 {
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  color: #262626;
}

.remove-indicator-btn {
  background: none;
  border: none;
  font-size: 16px;
  cursor: pointer;
  color: #8c8c8c;
  padding: 0;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: all 0.2s;
}

.remove-indicator-btn:hover {
  background: #ff4d4f;
  color: white;
}

.chart-wrapper {
  padding: 16px;
  transition: height 0.3s ease;
}

.loading-message,
.no-data-message,
.no-indicators-message {
  text-align: center;
  padding: 40px;
  color: #8c8c8c;
}

.no-indicators-message p {
  margin-bottom: 16px;
}

@media (max-width: 768px) {
  .indicators-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }
  
  .chart-wrapper {
    padding: 12px;
  }
}
</style>
